import React from 'react'
import PropTypes from 'prop-types'
import { ChevronRightIcon, Icon, NavLink } from 'src/components'
import { useIntl } from 'react-intl'
import noop from 'lodash/noop'
import styles from './BreadcrumbItem.scss'

/**
 * Item component used in Breadcrumbs.
 */
const BreadcrumbItem = ({ to, icon, title, useTitle = noop, preserveQueryParams, i18n, params, error, isLastItem }) => {
  // get intl
  const intl = useIntl()

  // get async title if any
  const asyncTitle = useTitle(params) || '...'

  // get i18n title if any
  const i18nTitle = i18n && intl.formatMessage({ id: i18n })

  // compute actual title
  const foundTitle = error || i18nTitle || title || asyncTitle

  // update document title with last breadcrumb item only
  React.useEffect(() => {
    if (isLastItem) {
      document.title = foundTitle + ' - Labguard'
    }
  }, [isLastItem, foundTitle])

  return (
    <div className={styles.item}>
      {icon && <Icon className={styles.icon} icon={icon} />}
      <NavLink
        className={styles.link}
        exact
        activeClassName={styles.active}
        to={to}
        preserveQueryParams={preserveQueryParams}
      >
        {foundTitle}
      </NavLink>
      {!isLastItem && <Icon className={styles.separator} icon={ChevronRightIcon} />}
    </div>
  )
}

BreadcrumbItem.propTypes = {
  /**
   * The state target name of the item.
   */
  to: PropTypes.string.isRequired,
  /**
   * The name of the icon to display.
   */
  icon: PropTypes.elementType,
  /**
   * The link title.
   */
  title: PropTypes.node,
  /**
   * The potential error to display instead of the title.
   */
  error: PropTypes.string,
  /**
   * The i18n key to use for the title.
   */
  i18n: PropTypes.string,
  /**
   * A hook function to get the title.
   */
  useTitle: PropTypes.func,
  /**
   * Whether or not to preserve query params
   */
  preserveQueryParams: PropTypes.bool,
  /**
   * The current route params provided to method useTitle.
   */
  params: PropTypes.object,
  /**
   * Whether the item is the last one of the breadcrumbs items.
   */
  isLastItem: PropTypes.bool,
}

export default BreadcrumbItem
